import { useState, useEffect } from 'react';
import { Check, AlertTriangle, ExternalLink, Crown, Zap, Award } from 'lucide-react';
import { api, Subscription, TierConfig } from '../lib/api';

interface BillingManagerProps {
  userRole: string;
  currentPlan: string;
}

interface PlanInfo {
  id: 'pro' | 'advanced' | 'platinum';
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  icon: typeof Crown;
  features: string[];
  highlighted?: boolean;
}

const PLAN_ORDER: Record<string, number> = { pro: 0, advanced: 1, platinum: 2 };

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function statusLabel(status: string): { text: string; className: string } {
  switch (status) {
    case 'active':
      return { text: 'Active', className: 'bg-green-100 text-green-800' };
    case 'trialing':
      return { text: 'Trial', className: 'bg-blue-100 text-blue-800' };
    case 'past_due':
      return { text: 'Past Due', className: 'bg-red-100 text-red-800' };
    case 'canceled':
      return { text: 'Canceled', className: 'bg-gray-100 text-gray-800' };
    case 'unpaid':
      return { text: 'Unpaid', className: 'bg-red-100 text-red-800' };
    case 'incomplete':
      return { text: 'Incomplete', className: 'bg-yellow-100 text-yellow-800' };
    default:
      return { text: status, className: 'bg-gray-100 text-gray-800' };
  }
}

function buildPlans(tiers: TierConfig): PlanInfo[] {
  return [
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: Number(tiers.pro_monthly_price) || 29,
      yearlyPrice: Number(tiers.pro_yearly_price) || 290,
      icon: Zap,
      features: tiers.pro_features ? tiers.pro_features.split('|') : [],
    },
    {
      id: 'advanced',
      name: 'Advanced',
      monthlyPrice: Number(tiers.advanced_monthly_price) || 79,
      yearlyPrice: Number(tiers.advanced_yearly_price) || 790,
      icon: Crown,
      highlighted: true,
      features: tiers.advanced_features ? tiers.advanced_features.split('|') : [],
    },
    {
      id: 'platinum',
      name: 'Platinum',
      monthlyPrice: Number(tiers.platinum_monthly_price) || 199,
      yearlyPrice: Number(tiers.platinum_yearly_price) || 1990,
      icon: Award,
      features: tiers.platinum_features ? tiers.platinum_features.split('|') : [],
    },
  ];
}

export default function BillingManager({ userRole, currentPlan }: BillingManagerProps) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [plans, setPlans] = useState<PlanInfo[]>([]);

  const isOwner = userRole === 'owner';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [sub, tiers] = await Promise.all([
        api.getSubscription(),
        api.getTierConfig(),
      ]);
      setSubscription(sub);
      setPlans(buildPlans(tiers));
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: 'pro' | 'advanced' | 'platinum') => {
    setUpgrading(plan);
    setError('');
    try {
      const { url } = await api.createCheckoutSession(plan, interval);
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setUpgrading(null);
    }
  };

  const handleManageSubscription = async () => {
    setError('');
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal');
    }
  };

  const activePlan = subscription?.plan || currentPlan;
  const activePlanOrder = PLAN_ORDER[activePlan] ?? 0;
  const hasPaidSubscription =
    subscription?.stripe_subscription_id &&
    subscription.status !== 'canceled';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Non-owner notice */}
      {!isOwner && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800">Contact your account owner to change plans.</p>
        </div>
      )}

      {/* Current Plan Banner */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Current Plan</h2>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-2xl font-bold text-gray-900 capitalize">{activePlan}</span>
              {subscription && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusLabel(subscription.status).className}`}>
                  {statusLabel(subscription.status).text}
                </span>
              )}
            </div>
            {subscription?.current_period_end && subscription.status !== 'canceled' && (
              <p className="text-sm text-gray-500 mt-1">
                {subscription.cancel_at_period_end
                  ? `Cancels on ${formatDate(subscription.current_period_end)}`
                  : `Next billing date: ${formatDate(subscription.current_period_end)}`}
              </p>
            )}
            {subscription?.cancel_at_period_end && (
              <div className="mt-2 flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Your subscription will cancel at the end of the current period.</span>
              </div>
            )}
            {subscription?.status === 'past_due' && (
              <div className="mt-2 flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Your payment is past due. Please update your payment method.</span>
              </div>
            )}
          </div>
          {hasPaidSubscription && isOwner && (
            <button
              onClick={handleManageSubscription}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Manage Subscription
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Billing Interval Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setInterval('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              interval === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              interval === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Yearly
            <span className="ml-1.5 text-xs text-green-600 font-semibold">Save ~17%</span>
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = activePlan === plan.id;
          const planOrder = PLAN_ORDER[plan.id];
          const isUpgrade = planOrder > activePlanOrder;
          const isDowngrade = planOrder < activePlanOrder;
          const PlanIcon = plan.icon;
          const price = interval === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
          const perMonth = interval === 'yearly' && plan.yearlyPrice > 0
            ? Math.round(plan.yearlyPrice / 12)
            : plan.monthlyPrice;

          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-lg shadow-sm border-2 p-6 flex flex-col ${
                plan.highlighted
                  ? 'border-blue-500 ring-1 ring-blue-500'
                  : isCurrentPlan
                    ? 'border-blue-200'
                    : 'border-gray-200'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
                    Popular
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  plan.id === 'pro'
                    ? 'bg-gray-100 text-gray-600'
                    : plan.id === 'advanced'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-amber-100 text-amber-600'
                }`}>
                  <PlanIcon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
              </div>

              {/* Price */}
              <div className="mb-6">
                {interval === 'yearly' ? (
                  <>
                    <span className="text-4xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500">/yr</span>
                    <div className="text-sm text-gray-500 mt-0.5">${perMonth}/mo</div>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-gray-900">${price}</span>
                    <span className="text-gray-500">/mo</span>
                  </>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      plan.id === 'platinum' ? 'text-amber-500' : plan.highlighted ? 'text-blue-500' : 'text-green-500'
                    }`} />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <div className="mt-auto">
                {isCurrentPlan ? (
                  <button disabled className="w-full py-2.5 px-4 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-500 bg-gray-50 cursor-not-allowed">
                    Current Plan
                  </button>
                ) : isUpgrade ? (
                  <button
                    onClick={() => isOwner && handleUpgrade(plan.id)}
                    disabled={!isOwner || upgrading !== null}
                    className={`w-full py-2.5 px-4 text-sm font-medium rounded-lg transition-colors ${
                      !isOwner
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : plan.id === 'platinum'
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : plan.highlighted
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-800 text-white hover:bg-gray-900'
                    } ${upgrading === plan.id ? 'opacity-75' : ''}`}
                  >
                    {upgrading === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Redirecting...
                      </span>
                    ) : (
                      'Upgrade'
                    )}
                  </button>
                ) : isDowngrade ? (
                  <button
                    onClick={() => isOwner && handleManageSubscription()}
                    disabled={!isOwner}
                    className={`w-full py-2.5 px-4 text-sm font-medium rounded-lg border-2 transition-colors ${
                      !isOwner
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Downgrade
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
